import React, { useState } from 'react';
import Image from '../../../components/AppImage';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const MediaGridItem = ({ 
  media, 
  isSelected, 
  onSelect, 
  onEdit, 
  onDelete,
  onToggleDownload 
}) => {
  const [showActions, setShowActions] = useState(false);

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1048576) return `${(bytes / 1024)?.toFixed(1)} KB`;
    return `${(bytes / 1048576)?.toFixed(1)} MB`;
  };

  const formatDate = (date) => {
    return new Date(date)?.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  return (
    <div 
      className="relative bg-card rounded-xl overflow-hidden border border-border transition-all duration-250 hover:shadow-warm-md group"
      onMouseEnter={() => setShowActions(true)}
      onMouseLeave={() => setShowActions(false)}
    >
      <div className="relative aspect-[4/3] bg-muted">
        <Image
          src={media?.thumbnail_url || media?.file_url}
          alt={media?.alt_text || `Media file: ${media?.file_name}`}
          className="w-full h-full object-cover"
        />
        
        {media?.media_type === 'video' && (
          <div className="absolute inset-0 flex items-center justify-center bg-black/30">
            <div className="w-12 h-12 md:w-16 md:h-16 rounded-full bg-primary/90 flex items-center justify-center">
              <Icon name="Play" size={24} color="white" />
            </div>
          </div>
        )}

        <div className="absolute top-2 left-2 right-2 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <button
              onClick={() => onSelect(media?.id)}
              className={`w-6 h-6 rounded border-2 flex items-center justify-center transition-all ${
                isSelected 
                  ? 'bg-primary border-primary' :'bg-white/90 border-white/90 hover:bg-primary hover:border-primary'
              }`}
            >
              {isSelected && <Icon name="Check" size={16} color="white" />}
            </button>
            
            <span className={`px-2 py-1 rounded text-xs font-medium ${
              media?.media_type === 'video' ?'bg-accent text-accent-foreground' :'bg-secondary text-secondary-foreground'
            }`}>
              {media?.media_type === 'video' ? 'Video' : 'Photo'}
            </span>
          </div>

          {!media?.download_enabled && (
            <div className="px-2 py-1 rounded bg-destructive/90 text-destructive-foreground text-xs font-medium">
              <Icon name="Lock" size={12} className="inline mr-1" />
              Locked
            </div>
          )}
        </div>

        {(showActions || window.innerWidth < 768) && (
          <div className="absolute bottom-2 right-2 flex gap-2">
            <Button
              variant="secondary"
              size="icon"
              onClick={() => onEdit(media)}
              className="w-8 h-8 shadow-warm-md"
            >
              <Icon name="Edit2" size={16} />
            </Button>
            <Button
              variant="destructive"
              size="icon"
              onClick={() => onDelete(media?.id)}
              className="w-8 h-8 shadow-warm-md"
            >
              <Icon name="Trash2" size={16} />
            </Button>
          </div>
        )}
      </div>
      <div className="p-3 md:p-4">
        <h3 className="font-medium text-sm md:text-base mb-2 line-clamp-1">
          {media?.title || media?.file_name}
        </h3>
        
        <div className="flex items-center justify-between text-xs md:text-sm text-muted-foreground mb-3">
          <span className="data-text">{formatFileSize(media?.file_size || 0)}</span>
          <span>{formatDate(media?.created_at || media?.uploadDate)}</span>
        </div>

        <div className="flex items-center justify-between pt-3 border-t border-border">
          <div className="flex items-center gap-4 text-xs md:text-sm">
            <div className="flex items-center gap-1">
              <Icon name="Eye" size={14} className="text-muted-foreground" />
              <span className="data-text">{media?.view_count || 0}</span>
            </div>
            <div className="flex items-center gap-1">
              <Icon name="Download" size={14} className="text-muted-foreground" />
              <span className="data-text">{media?.download_count || 0}</span>
            </div>
          </div>

          <button
            onClick={() => onToggleDownload(media?.id)}
            className={`p-1.5 rounded transition-all ${
              media?.download_enabled 
                ? 'text-success hover:bg-success/10' :'text-muted-foreground hover:bg-muted'
            }`}
          >
            <Icon name={media?.download_enabled ? 'Unlock' : 'Lock'} size={16} />
          </button>
        </div>
      </div>
    </div>
  );
};

export default MediaGridItem;